

























































































































// Vue-related and Decorators
import { Component, Watch } from 'vue-property-decorator';

// Models
import type { Phase } from '@/models/phase';
import type { ExtendedTrade } from '@/models/trade';
import type { AppliedLesson } from '@/models/appliedlesson';
import type { Project } from '@/models/project';

// Components
import ColumnsFilter from '@/components/worksheets/ColumnsFilter.vue';
import FormDynamicFieldMultiselect from '@/components/form/FormDynamicFieldMultiselect.vue';
import FormPhaseMultiselect from '@/components/form/FormPhaseMultiselect.vue';
import PhaseWorksheet from '@/components/projects/fields/PhaseWorksheet.vue';
import DetailedTrade from '@/components/worksheets/trade/DetailedTrade.vue';
import AppliedLessonTable from '@/components/worksheets/AppliedLessonTable.vue';
import WorksheetUpdateLessonModal from '@/components/worksheets/WorksheetUpdateLessonModal.vue';
import FormTopicPicker from '@/components/form/FormTopicPicker.vue';
import ProjectViewFilterPanel from '../filters/ProjectViewFilterPanel.vue';

// Web Services
import { appliedLessonWebservice } from '@/webservices/appliedlesson';

// Helpers and Miscellaneous
import { createAppliedLessonsFilter } from '@/helpers/createAppliedLessonsFilter';
import { AppliedLessonType } from '@/enums/AppliedLessonType';
import ProjectViewListBase from './ProjectViewListBase'; // Update the path accordingly
import { debounce } from 'lodash';
import { showSuccessNotification } from '@/helpers/showSuccessNotification';
import {
    filterAppliedLessonsResolved,
    ResolvedEnum,
} from '@/helpers/filter/appliedLessons/filterAppliedLessonsResolved';
import { CheckedEnum, filterAppliedLessonsChecked } from '@/helpers/filter/appliedLessons/filterAppliedLessonsChecked';

@Component({
    name: 'ProjectViewTradesList',
    components: {
        FormPhaseMultiselect,
        FormDynamicFieldMultiselect,
        ColumnsFilter,
        PhaseWorksheet,
        DetailedTrade,
        AppliedLessonTable,
        WorksheetUpdateLessonModal,
        FormTopicPicker,
        ProjectViewFilterPanel,
    },
})
export default class ProjectViewTradesList extends ProjectViewListBase {
    get filteredTrades() {
        return this.trades.filter((t) => this.selectedTrades.some((st) => st.id === t.id));
    }

    get trades() {
        return this.prepareTrades(this.project.trades);
    }
    private AppliedLessonType = AppliedLessonType;

    private toggledTrades: number[] = [];
    private toggledPhases: number[] = [];

    private appliedLessonToUpdate: AppliedLesson = {} as AppliedLesson;

    private selectedTabs: string[] = ['trades'];

    private DEBOUNCE_APPLY_UPDATE_INTERVAL = 0; // milliseconds
    private DEBOUNCE_RELOAD_WORKSHEET_INTERVAL = 10; // milliseconds
    private DEBOUNCE_UPDATE_NOTIFICATION_INTERVAL = 2000; // milliseconds

    private debouncedUpdate = debounce(this.applyUpdate, this.DEBOUNCE_APPLY_UPDATE_INTERVAL);
    private debouncedReload = debounce(this.reloadWorksheet, this.DEBOUNCE_RELOAD_WORKSHEET_INTERVAL);
    private debouncedUpdateNotification = debounce(this.updateNotification, this.DEBOUNCE_UPDATE_NOTIFICATION_INTERVAL);

    private isUpdating: boolean = false;

    // selectedTrades
    mounted() {
        this.loadPhases();
        this.loadTopics();
        this.resetFilter();
    }

    private async applyUpdate(lesson: AppliedLesson) {
        await appliedLessonWebservice.update(lesson as AppliedLesson).catch(() => {
            this.showErrorNotification('toastMessages.error.worksheet');
        });
        this.debouncedUpdateNotification();
        this.debouncedReload();
    }

    private resetTrades() {
        const clonedTrades = this.prepareTrades(this.project.trades);

        this.selectedTrades = clonedTrades
            .map((trade) => {
                trade.phases = trade.phases.filter((phase) => {
                    let selected = false;
                    this.selectedPhases.forEach((selectedPhase) => {
                        if (selectedPhase.id === phase.id) {
                            selected = true;
                            return;
                        }
                    });
                    return selected;
                });
                return trade;
            })
            // Filter out Trades with no Phases or no applied Lessons
            .filter((trade: ExtendedTrade) => {
                return (
                    trade.phases.length !== 0 &&
                    trade.phases.find((p) => p.worksheet && p.worksheet.applied_lessons.length > 0)
                );
            });
    }

    private resetFilter() {
        this.selectedTopics = [];
        this.filterResolved = ResolvedEnum.NotResolved;
        this.filterChecked = CheckedEnum.All;
        this.tableColumns = createAppliedLessonsFilter(this.companySetting);
        this.selectedPhases = this.phases.filter((phase: Phase) => phase.worksheets!.length > 0) as Phase[];
        this.resetTrades();
        this.selectedTabs = ['trades'];

        if (this.companySetting.feature_dynamic_worksheet_field) {
            this.fetchAllDynamicFields();
            this.selectedDynamicFields = this.dynamicFields;
        }
    }

    @Watch('project')
    private onProjectChanged(newProject: Project, oldProject: Project | null) {
        if (!oldProject || newProject.id !== oldProject.id) {
            this.resetFilter();
        }
    }

    private toggleUpdateAppliedLessonModal(value) {
        this.appliedLessonToUpdate = value;
        let worksheet = null;
        for (const trade of this.project.trades as any[]) {
            for (const phase of trade.phases) {
                if (phase.worksheet && phase.worksheet.id === value.worksheet_id) {
                    worksheet = phase.worksheet;
                    break;
                }
            }
            if (worksheet) {
                break;
            }
        }
        this.toggleModal(WorksheetUpdateLessonModal, {
            type: AppliedLessonType.AppliedLesson,
            worksheet,
            saved: this.refreshSelectedProject,
            appliedLessonToUpdate: this.appliedLessonToUpdate,
        });
    }

    private async reloadWorksheet() {
        this.isUpdating = true;
        await this.refreshSelectedProject();
        this.isUpdating = false;
    }

    private updateNotification() {
        showSuccessNotification('toastMessages.update.worksheet');
    }

    private prepareTrades(trades) {
        let clonedTrades = JSON.parse(JSON.stringify(trades));

        clonedTrades = clonedTrades
            .map((trade: ExtendedTrade) => {
                // Open all Trades by default
                // this.toggledTrades.push(trade.id);

                trade.phases = trade.phases
                    // Filter out Phases with no applied Lessons
                    .filter((phase) => phase.worksheet && phase.worksheet.applied_lessons.length > 0)
                    // Filter our by selected Phases
                    .filter((phase) => this.selectedPhases.some((p) => p.id === phase.id))
                    .map((phase) => {
                        phase.worksheet.applied_lessons = phase.worksheet.applied_lessons
                            // Filter out by Resolved/Open state
                            .filter((appliedLesson) => {
                                return filterAppliedLessonsResolved(appliedLesson, this.filterResolved);
                            })
                            .filter((appliedLesson) => {
                                return filterAppliedLessonsChecked(appliedLesson, this.filterChecked);
                            })
                            // Dynamic Field
                            .filter((appliedLesson) => {
                                if (!this.tableColumns || !this.tableColumns.dynamic_field.display) {
                                    return true;
                                }

                                // Check the applied lesson and its sub-lessons
                                return this.hasMatchingDynamicField(appliedLesson);
                            })
                            // Filter out by selected topics
                            .filter((appliedLesson) => {
                                if (this.selectedTopics.length === 0) {
                                    return true;
                                }
                                return (
                                    appliedLesson.lesson.topics &&
                                    this.selectedTopics.some((topic) => {
                                        if (appliedLesson.lesson.topics.length > 0) {
                                            return topic.id === appliedLesson.lesson.topics[0].id;
                                        }
                                        return false;
                                    })
                                );
                            });
                        phase.worksheet.applied_lessons = this.mapUsersRec(phase.worksheet.applied_lessons);

                        return phase;
                    })
                    // Filter out remaining Phases with no applied Lessons
                    .filter((phase) => phase.worksheet && phase.worksheet.applied_lessons.length > 0);

                return trade;
            })
            // Filter out Trades with no Phases or no applied Lessons
            .filter((trade: ExtendedTrade) => {
                return trade.phases.length !== 0 && trade.phases.find((p) => p.worksheet.applied_lessons.length > 0);
            });
        if (this.selectedTrades.length === 0 && clonedTrades.length !== 0) {
            // TODO: what if clonedTrades is empty?
            this.selectedTrades = JSON.parse(JSON.stringify(clonedTrades));
        }
        return clonedTrades;
    }
}
